import PolicyIcon from "@mui/icons-material/Policy";
import { Box, Stack, Typography } from "@mui/material";
import { type FC } from "react";

import { CollapsedText } from "~/components/UI/CollapsedText";
import { RDA_COLLAPSED_MAX_HEIGHT_PX } from "~/components/UI/CollapsedText/constants";
import {
  alertIcon,
  alertTitle,
} from "~/feature/event/components/AlertSection/constants";
import { AlertStatusEnum } from "~/feature/event/components/AlertSection/types";
import { EventPageSections } from "~/feature/event/constants";
import { type IResolutionRules } from "~/types/event";

import * as Styled from "./AlertSection.styles";

interface IAlertSectionProps {
  resolutionRules?: IResolutionRules;
  matchConfidence?: number;
}

export const AlertSection: FC<IAlertSectionProps> = ({ resolutionRules }) => {
  if (!resolutionRules) {
    return null;
  }

  const Icon = alertIcon[resolutionRules.status];

  return (
    <Box>
      <Styled.Root
        $status={resolutionRules.status}
        component="section"
        id={EventPageSections.RDA}
      >
        <Stack
          sx={{
            gap: 1,
            maxWidth: "100%"
          }}>
          <Styled.Header>
            <Styled.SectionTitle
              component="h2"
              variant="subtitle1"
              sx={{ fontWeight: 600 }}
            >
              <PolicyIcon />
              PredictionHero - Resolution Divergence Alerts (RDA)
            </Styled.SectionTitle>
          </Styled.Header>
          <CollapsedText
            maxCollapsedHeightPx={RDA_COLLAPSED_MAX_HEIGHT_PX}
            component="div"
            sectionIdToExpand={EventPageSections.RDA}
          >
            {resolutionRules.status === AlertStatusEnum.UnifiedResolution && (
              <Styled.RulesWrapper>
                <Styled.AlertSubtitle
                  component="h3"
                  variant="body2"
                  sx={{ fontWeight: 600 }}
                >
                  <Icon color="success" />
                  {alertTitle[resolutionRules.status]}
                </Styled.AlertSubtitle>
                {!!resolutionRules.consistencyConfirmation && (
                  <Typography variant="caption">
                    {resolutionRules.consistencyConfirmation}
                  </Typography>
                )}
                {resolutionRules.primaryResolutionSource && (
                  <Typography variant="caption">
                    <Styled.RuleTitle component="span" sx={{ fontWeight: 700 }}>
                      Primary resolution logic:
                    </Styled.RuleTitle>{" "}
                    {resolutionRules.primaryResolutionSource}
                  </Typography>
                )}
                {!!resolutionRules.coreResolutionLogic?.length && (
                  <Styled.RuleBlock>
                    <Styled.RuleTitle
                      component="h4"
                      variant="caption"
                      sx={{ fontWeight: 700 }}
                    >
                      Core resolution logic:
                    </Styled.RuleTitle>
                    <Styled.RuleList>
                      {resolutionRules.coreResolutionLogic.map(
                        (item, index) => (
                          <li key={index}>{item}</li>
                        )
                      )}
                    </Styled.RuleList>
                  </Styled.RuleBlock>
                )}
                {!!resolutionRules.edgeCases?.length && (
                  <Styled.RuleBlock>
                    <Styled.RuleTitle
                      component="h4"
                      variant="caption"
                      sx={{ fontWeight: 700 }}
                    >
                      Edge cases & clarifications:
                    </Styled.RuleTitle>
                    <Styled.RuleList>
                      {resolutionRules.edgeCases.map(({ name, value }) => (
                        <li key={name}>
                          {name}: {value}
                        </li>
                      ))}
                    </Styled.RuleList>
                  </Styled.RuleBlock>
                )}
                {!!resolutionRules.timing && (
                  <Typography variant="caption">
                    <Styled.RuleTitle component="span" sx={{ fontWeight: 700 }}>
                      Timing:
                    </Styled.RuleTitle>{" "}
                    {resolutionRules.timing}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: 12
                  }}>
                  Our PredictionHero Resolution Divergence Alerts (RDA) are
                  there to help users identify potential differences across
                  platforms. They do not replace or supersede the official rules
                  and description of any prediction market. Users are solely
                  responsible for reviewing and understanding the applicable
                  rules and resolution criteria before placing any trade or
                  bet. If you notice a potential inconsistency, discrepancy, or
                  error in an alert, please report it to our team so we can
                  review and improve the accuracy of our data.
                </Typography>
              </Styled.RulesWrapper>
            )}
            {resolutionRules.status === AlertStatusEnum.Divergence && (
              <Styled.RulesWrapper>
                <Styled.AlertSubtitle
                  component="h3"
                  variant="body2"
                  sx={{ fontWeight: 600 }}
                >
                  <Icon color="error" />
                  {alertTitle[resolutionRules.status]}
                </Styled.AlertSubtitle>
                {!!resolutionRules.issue && (
                  <Typography variant="caption">
                    <Styled.RuleTitle component="span" sx={{ fontWeight: 700 }}>
                      Issue:
                    </Styled.RuleTitle>{" "}
                    {resolutionRules.issue}
                  </Typography>
                )}
                {!!resolutionRules.heroTip && (
                  <Typography variant="caption">
                    <Styled.RuleTitle component="span" sx={{ fontWeight: 700 }}>
                      Hero tip:
                    </Styled.RuleTitle>{" "}
                    {resolutionRules.heroTip}
                  </Typography>
                )}
                {!!resolutionRules.divergences?.length && (
                  <Styled.RuleBlock>
                    <Styled.RuleTitle
                      component="h4"
                      variant="caption"
                      sx={{ fontWeight: 700 }}
                    >
                      Critical divergence points:
                    </Styled.RuleTitle>
                    <Styled.RuleList>
                      {resolutionRules.divergences.map(
                        ({ platform, summary }) => (
                          <li key={platform}>
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                display: "inline",
                                fontWeight: 600
                              }}>
                              {platform}:
                            </Typography>{" "}
                            {summary}
                          </li>
                        )
                      )}
                    </Styled.RuleList>
                  </Styled.RuleBlock>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: 12
                  }}>
                  Our PredictionHero Resolution Divergence Alerts (RDA) are
                  there to help users identify potential differences across
                  platforms. They do not replace or supersede the official rules
                  and description of any prediction market. Users are solely
                  responsible for reviewing and understanding the applicable
                  rules and resolution criteria before placing any trade or
                  bet. If you notice a potential inconsistency, discrepancy, or
                  error in an alert, please report it to our team so we can
                  review and improve the accuracy of our data.
                </Typography>
              </Styled.RulesWrapper>
            )}
          </CollapsedText>
        </Stack>
      </Styled.Root>
    </Box>
  );
};
